import { useState, useEffect } from "react";
import {
  BarChart3, TrendingUp, Phone, Activity, FileSpreadsheet,
  MessageCircle, CreditCard, Wallet, Building, DollarSign, Trophy, Calendar,
  Zap, Server, CheckCircle, XCircle, AlertTriangle, Globe
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { API, apiCall, formatDate } from "@/lib/api";

const StatCard = ({ label, value, icon: Icon, color, sub }) => (
  <div className="bg-zinc-900/60 border border-white/10 rounded-lg p-4 flex items-center gap-3" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className={`p-2.5 rounded-lg ${color}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <p className="text-2xl font-bold text-white">{typeof value === "number" ? value.toLocaleString("es-AR") : value}</p>
      <p className="text-xs text-zinc-500">{label}</p>
      {sub && <p className="text-[10px] text-zinc-600 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const BarChart = ({ data, label, color, prefix = "", showTop = 0 }) => {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d.value), 1);
  const displayData = showTop > 0 ? data.slice(-showTop) : data;
  return (
    <div className="space-y-1.5">
      {label && <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">{label}</p>}
      <div className="flex items-end gap-1 h-28">
        {displayData.map((d, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-0.5 min-w-0">
            <span className="text-[9px] text-zinc-500 font-mono truncate w-full text-center">
              {d.value > 0 ? `${prefix}${d.value.toLocaleString("es-AR")}` : ""}
            </span>
            <div
              className={`w-full rounded-t ${color} transition-all duration-500`}
              style={{ height: `${Math.max((d.value / max) * 100, 2)}%`, minHeight: d.value > 0 ? "4px" : "2px" }}
            />
            <span className="text-[9px] text-zinc-600 truncate w-full text-center">{d.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const MiniBar = ({ value, max, color }) => (
  <div className="h-2 bg-zinc-800 rounded-full flex-1 overflow-hidden">
    <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${max > 0 ? Math.max((value / max) * 100, 1) : 0}%` }} />
  </div>
);

const DonutStat = ({ items, total }) => {
  if (total === 0) return <p className="text-zinc-600 text-sm text-center py-6">Sin datos</p>;
  return (
    <div className="space-y-2">
      {items.map(({ label, value, color }) => (
        <div key={label} className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${color}`} />
          <span className="text-sm text-zinc-300 flex-1">{label}</span>
          <span className="text-sm font-bold text-white">{value.toLocaleString("es-AR")}</span>
          <span className="text-xs text-zinc-500 w-12 text-right">{total > 0 ? `${((value / total) * 100).toFixed(1)}%` : "0%"}</span>
        </div>
      ))}
    </div>
  );
};

const MONTH_NAMES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const monthLabel = (m) => { const p = m.split("-"); return MONTH_NAMES[parseInt(p[1]) - 1] || m; };
const dayLabel = (d) => { const p = d.split("-"); return `${p[2]}/${p[1]}`; };

const PROVIDER_LABELS = {
  rapidapi: "API B", rapidapi_v2: "API C", rapidapi_both: "B+C",
  globalwpp: "API A", unknown: "Desconocido"
};
const PROVIDER_COLORS = {
  rapidapi: "bg-blue-500", rapidapi_v2: "bg-cyan-500", rapidapi_both: "bg-violet-500",
  globalwpp: "bg-amber-500", unknown: "bg-zinc-600"
};

export const UsageTab = () => {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchUsage(); }, []);

  const fetchUsage = async () => {
    setLoading(true);
    try {
      const { ok, data } = await apiCall("GET", `${API}/my/usage`);
      if (ok) setUsage(data);
    } catch {}
    finally { setLoading(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500" />
    </div>
  );

  if (!usage) return <p className="text-zinc-500 text-center py-10">Error al cargar estadisticas</p>;

  const { totals, monthly, daily, top_campaigns, recent_activity, is_admin, company_ranking, monthly_costs, wa_monthly, api_usage, provider_stats, provider_monthly } = usage;

  return (
    <div className="space-y-5" data-testid="usage-dashboard">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Campanas" value={totals.campaigns} icon={FileSpreadsheet} color="bg-violet-500/15 text-violet-400" />
        <StatCard label="Numeros" value={totals.numbers} icon={Phone} color="bg-blue-500/15 text-blue-400" />
        <StatCard label="Celulares" value={totals.celular} icon={TrendingUp} color="bg-emerald-500/15 text-emerald-400"
          sub={totals.numbers > 0 ? `${((totals.celular / totals.numbers) * 100).toFixed(1)}%` : ""} />
        <StatCard label="WhatsApp" value={totals.whatsapp_si} icon={MessageCircle} color="bg-green-500/15 text-green-400"
          sub={totals.celular > 0 ? `${((totals.whatsapp_si / totals.celular) * 100).toFixed(1)}% de cel` : ""} />
        <StatCard label="Consumo 30d" value={`$${totals.cost_30d?.toLocaleString("es-AR") || 0}`} icon={DollarSign} color="bg-amber-500/15 text-amber-400" />
      </div>

      {/* Daily Charts Row */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Daily Cost */}
        {daily && daily.length > 0 && daily.some(d => d.cost > 0) && (
          <div className="bg-zinc-900/60 border border-white/10 rounded-lg p-4">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider flex items-center gap-2">
                <DollarSign className="w-3.5 h-3.5" />Costo diario (30 dias)
              </p>
              <span className="text-xs text-amber-400 font-mono">Total: ${totals.cost_30d?.toLocaleString("es-AR") || 0}</span>
            </div>
            <BarChart data={daily.map(d => ({ label: dayLabel(d.date), value: d.cost }))} color="bg-amber-500" prefix="$" showTop={30} />
          </div>
        )}
        {/* Daily Numbers */}
        {daily && daily.length > 0 && daily.some(d => d.numbers > 0) && (
          <div className="bg-zinc-900/60 border border-white/10 rounded-lg p-4">
            <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider flex items-center gap-2 mb-1">
              <Phone className="w-3.5 h-3.5" />Numeros procesados (30 dias)
            </p>
            <BarChart data={daily.map(d => ({ label: dayLabel(d.date), value: d.numbers }))} color="bg-blue-500" showTop={30} />
          </div>
        )}
      </div>

      {/* Monthly Charts Row */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="bg-zinc-900/60 border border-white/10 rounded-lg p-4">
          <BarChart data={monthly.map(m => ({ label: monthLabel(m.month), value: m.campaigns }))} label="Campanas por mes" color="bg-violet-500" />
        </div>
        <div className="bg-zinc-900/60 border border-white/10 rounded-lg p-4">
          <BarChart data={monthly.map(m => ({ label: monthLabel(m.month), value: m.total }))} label="Numeros por mes" color="bg-blue-500" />
        </div>
        {/* Monthly Cost */}
        {monthly_costs && monthly_costs.length > 0 && (
          <div className="bg-zinc-900/60 border border-white/10 rounded-lg p-4">
            <BarChart data={monthly_costs.map(m => ({ label: monthLabel(m.month), value: m.cost }))} label="Costo mensual ($ARS)" color="bg-amber-500" prefix="$" />
          </div>
        )}
        {/* Distribution */}
        <div className="bg-zinc-900/60 border border-white/10 rounded-lg p-4">
          <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3">Distribucion</p>
          <DonutStat
            total={totals.numbers}
            items={[
              { label: "Celulares", value: totals.celular, color: "bg-emerald-500" },
              { label: "Fijos", value: totals.fijo, color: "bg-blue-500" },
              { label: "Invalidos", value: totals.invalido, color: "bg-rose-500" },
              ...(totals.whatsapp_si > 0 ? [{ label: "WA SI", value: totals.whatsapp_si, color: "bg-green-500" }] : []),
              ...(totals.whatsapp_no > 0 ? [{ label: "WA NO", value: totals.whatsapp_no, color: "bg-zinc-600" }] : []),
            ]}
          />
        </div>
      </div>

      {/* WhatsApp Success Rate by Month */}
      {wa_monthly && wa_monthly.length > 0 && (
        <div className="bg-zinc-900/60 border border-white/10 rounded-lg p-4" data-testid="wa-monthly-stats">
          <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3 flex items-center gap-2">
            <MessageCircle className="w-3.5 h-3.5" />Tasa de exito WhatsApp por mes
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-zinc-500 text-xs border-b border-zinc-800">
                  <th className="text-left py-2 pl-2">Mes</th>
                  <th className="text-right py-2">Total</th>
                  <th className="text-right py-2">SI</th>
                  <th className="text-right py-2">NO</th>
                  <th className="text-right py-2">Error</th>
                  <th className="text-right py-2 pr-2">% Exito</th>
                </tr>
              </thead>
              <tbody>
                {wa_monthly.map((m) => {
                  const pct = m.total > 0 ? ((m.si / m.total) * 100).toFixed(1) : "0.0";
                  return (
                    <tr key={m.month} className="border-b border-zinc-800/40 hover:bg-zinc-800/30">
                      <td className="py-2 pl-2 text-zinc-300 font-medium">{monthLabel(m.month)} {m.month.split("-")[0]}</td>
                      <td className="py-2 text-right text-zinc-400 font-mono">{m.total.toLocaleString("es-AR")}</td>
                      <td className="py-2 text-right text-green-400 font-mono">{m.si.toLocaleString("es-AR")}</td>
                      <td className="py-2 text-right text-zinc-500 font-mono">{m.no.toLocaleString("es-AR")}</td>
                      <td className="py-2 text-right text-rose-400 font-mono">{m.err.toLocaleString("es-AR")}</td>
                      <td className="py-2 text-right pr-2">
                        <Badge className={`text-[10px] font-bold ${parseFloat(pct) >= 50 ? "bg-green-500/15 text-green-400" : "bg-amber-500/15 text-amber-400"}`}>
                          {pct}%
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* API Usage (for companies with API keys) */}
      {api_usage && (
        <div className="bg-zinc-900/60 border border-white/10 rounded-lg p-4" data-testid="api-usage-section">
          <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-4 flex items-center gap-2">
            <Globe className="w-3.5 h-3.5" />Consumo de API
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatCard label="Llamadas 30d" value={api_usage.total_calls} icon={Zap} color="bg-cyan-500/15 text-cyan-400" />
            <StatCard label="Numeros via API" value={api_usage.total_phones} icon={Phone} color="bg-blue-500/15 text-blue-400" />
          </div>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            {api_usage.daily && api_usage.daily.some(d => d.calls > 0) && (
              <div>
                <BarChart data={api_usage.daily.map(d => ({ label: dayLabel(d.date), value: d.calls }))} label="Llamadas API diarias" color="bg-cyan-500" showTop={30} />
              </div>
            )}
            {api_usage.monthly && api_usage.monthly.length > 0 && (
              <div>
                <BarChart data={api_usage.monthly.map(m => ({ label: monthLabel(m.month), value: m.calls }))} label="Llamadas API mensuales" color="bg-cyan-600" />
              </div>
            )}
          </div>
          {api_usage.by_endpoint && api_usage.by_endpoint.length > 0 && (
            <div>
              <p className="text-xs text-zinc-500 mb-2">Por endpoint</p>
              <div className="space-y-1.5">
                {api_usage.by_endpoint.map((ep) => (
                  <div key={ep.endpoint} className="flex items-center gap-3 text-sm">
                    <code className="text-xs text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded w-48 truncate">{ep.endpoint}</code>
                    <MiniBar value={ep.calls} max={api_usage.by_endpoint[0]?.calls || 1} color="bg-cyan-500" />
                    <span className="text-xs text-zinc-400 font-mono w-16 text-right">{ep.calls} calls</span>
                    <span className="text-xs text-zinc-500 font-mono w-20 text-right">{ep.phones} nums</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Superadmin: Provider Consumption */}
      {is_admin && provider_stats && provider_stats.length > 0 && (
        <div className="bg-zinc-900/60 border border-white/10 rounded-lg p-4" data-testid="provider-consumption">
          <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-4 flex items-center gap-2">
            <Server className="w-3.5 h-3.5" />Consumo por Proveedor RapidAPI
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
            {provider_stats.map((ps) => {
              const pLabel = PROVIDER_LABELS[ps.provider] || ps.provider;
              const pColor = PROVIDER_COLORS[ps.provider] || "bg-zinc-600";
              const pct = ps.total_numbers > 0 ? ((ps.si / ps.total_numbers) * 100).toFixed(1) : "0.0";
              return (
                <div key={ps.provider} className="bg-zinc-800/60 border border-zinc-700/50 rounded-lg p-4" data-testid={`provider-stat-${ps.provider}`}>
                  <div className="flex items-center justify-between mb-3">
                    <Badge className={`text-xs font-bold px-2 py-0.5 ${pColor}/15 text-white border ${pColor}/20`}>{pLabel}</Badge>
                    <Badge className="text-[10px] bg-zinc-700/50 text-zinc-400">{ps.campaigns} campanas</Badge>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-500">Total verificados</span>
                      <span className="text-white font-bold font-mono">{ps.total_numbers.toLocaleString("es-AR")}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-3.5 h-3.5 text-green-400" />
                      <span className="text-xs text-zinc-400 flex-1">SI (con WA)</span>
                      <span className="text-green-400 font-mono text-sm font-bold">{ps.si.toLocaleString("es-AR")}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <XCircle className="w-3.5 h-3.5 text-zinc-500" />
                      <span className="text-xs text-zinc-400 flex-1">NO (sin WA)</span>
                      <span className="text-zinc-400 font-mono text-sm">{ps.no.toLocaleString("es-AR")}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-3.5 h-3.5 text-rose-400" />
                      <span className="text-xs text-zinc-400 flex-1">Errores</span>
                      <span className="text-rose-400 font-mono text-sm">{ps.err.toLocaleString("es-AR")}</span>
                    </div>
                    <div className="mt-2 pt-2 border-t border-zinc-700/50 flex justify-between items-center">
                      <span className="text-xs text-zinc-500">Tasa exito</span>
                      <Badge className={`text-xs font-bold ${parseFloat(pct) >= 50 ? "bg-green-500/15 text-green-400" : "bg-amber-500/15 text-amber-400"}`}>{pct}%</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-zinc-500">Completadas</span>
                      <span className="text-xs text-emerald-400 font-mono">{ps.completed}/{ps.campaigns}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Provider monthly trend */}
          {provider_monthly && provider_monthly.length > 0 && (
            <div>
              <p className="text-xs text-zinc-500 mb-2">Numeros verificados por proveedor por mes</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-zinc-500 text-xs border-b border-zinc-800">
                      <th className="text-left py-2 pl-2">Mes</th>
                      <th className="text-left py-2">Proveedor</th>
                      <th className="text-right py-2">Campanas</th>
                      <th className="text-right py-2 pr-2">Numeros</th>
                    </tr>
                  </thead>
                  <tbody>
                    {provider_monthly.map((pm, i) => (
                      <tr key={i} className="border-b border-zinc-800/40 hover:bg-zinc-800/30">
                        <td className="py-1.5 pl-2 text-zinc-400 text-xs">{monthLabel(pm.month)} {pm.month.split("-")[0]}</td>
                        <td className="py-1.5">
                          <Badge className={`text-[10px] ${PROVIDER_COLORS[pm.provider] || "bg-zinc-600"}/15 text-white`}>
                            {PROVIDER_LABELS[pm.provider] || pm.provider}
                          </Badge>
                        </td>
                        <td className="py-1.5 text-right text-zinc-400 font-mono">{pm.campaigns}</td>
                        <td className="py-1.5 text-right pr-2 text-white font-mono font-bold">{pm.numbers.toLocaleString("es-AR")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Top Campaigns */}
      {top_campaigns && top_campaigns.length > 0 && (
        <div className="bg-zinc-900/60 border border-white/10 rounded-lg p-4" data-testid="top-campaigns">
          <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Trophy className="w-3.5 h-3.5" />Campanas mas grandes
          </p>
          <div className="space-y-2">
            {top_campaigns.map((c, i) => {
              const maxNum = top_campaigns[0]?.total || 1;
              return (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs font-bold text-zinc-600 w-5 text-right">{i + 1}</span>
                  <span className="text-sm text-zinc-300 w-40 truncate">{c.name}</span>
                  <MiniBar value={c.total} max={maxNum} color="bg-violet-500" />
                  <span className="text-xs text-white font-mono w-16 text-right">{c.total.toLocaleString("es-AR")}</span>
                  <span className="text-[10px] text-zinc-600 w-20 text-right">{c.date}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Admin: Company Ranking */}
      {is_admin && company_ranking && company_ranking.length > 0 && (
        <div className="bg-zinc-900/60 border border-white/10 rounded-lg p-4" data-testid="company-ranking">
          <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Building className="w-3.5 h-3.5" />Ranking de Empresas
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-zinc-500 text-xs border-b border-zinc-800">
                  <th className="text-left py-2 pl-2">#</th>
                  <th className="text-left py-2">Empresa</th>
                  <th className="text-right py-2">Campanas</th>
                  <th className="text-right py-2">Numeros</th>
                  <th className="text-right py-2">Gastado</th>
                  <th className="text-right py-2 pr-2">Saldo</th>
                </tr>
              </thead>
              <tbody>
                {company_ranking.map((co, i) => (
                  <tr key={co.company_id} className="border-b border-zinc-800/40 hover:bg-zinc-800/30">
                    <td className="py-2 pl-2 text-zinc-600 font-bold">{i + 1}</td>
                    <td className="py-2 text-white font-medium truncate max-w-[200px]">{co.name}</td>
                    <td className="py-2 text-right text-zinc-300">{co.campaigns}</td>
                    <td className="py-2 text-right text-zinc-300 font-mono">{co.numbers.toLocaleString("es-AR")}</td>
                    <td className="py-2 text-right text-amber-400 font-mono">${co.total_cost.toLocaleString("es-AR")}</td>
                    <td className="py-2 text-right pr-2 font-mono">
                      <span className={co.balance > 0 ? "text-emerald-400" : "text-rose-400"}>${co.balance.toLocaleString("es-AR")}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Activity */}
      {recent_activity && recent_activity.length > 0 && (
        <div className="bg-zinc-900/60 border border-white/10 rounded-lg p-4" data-testid="recent-activity">
          <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Activity className="w-4 h-4" />Actividad Reciente
          </p>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {recent_activity.map((log, i) => (
              <div key={log.log_id || i} className="flex items-center gap-3 text-sm border-b border-zinc-800/50 pb-2">
                <span className="text-[11px] text-zinc-600 font-mono w-28 shrink-0">{formatDate(log.timestamp)}</span>
                <Badge variant="outline" className="text-[10px] border-zinc-700 text-zinc-400 shrink-0">
                  {log.action === "campaign_completed" ? "Completada" : log.action === "campaign_created" ? "Creada" : log.action === "balance_adjusted" ? "Ajuste" : log.action}
                </Badge>
                <span className="text-zinc-300 truncate flex-1">{log.details || ""}</span>
                {log.cost_ars > 0 && <span className="text-amber-400 text-xs font-mono shrink-0">-${log.cost_ars.toFixed(2)}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payment History */}
      <PaymentHistory />
    </div>
  );
};

const PaymentHistory = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiCall("GET", `${API}/payments/history`).then(({ ok, data }) => {
      if (ok) setPayments(data.payments || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading || payments.length === 0) return null;

  return (
    <div className="bg-zinc-900/60 border border-white/10 rounded-lg p-4" data-testid="payment-history">
      <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3 flex items-center gap-2">
        <CreditCard className="w-4 h-4" />Historial de Pagos
      </p>
      <div className="space-y-2 max-h-72 overflow-y-auto">
        {payments.map((pay) => (
          <div key={pay.payment_ref} className="flex items-center gap-3 text-sm border-b border-zinc-800/50 pb-2" data-testid={`payment-${pay.payment_ref}`}>
            <span className="text-[11px] text-zinc-600 font-mono w-28 shrink-0">{formatDate(pay.created_at)}</span>
            <Badge className={`text-[10px] shrink-0 ${pay.status === "approved" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : pay.status === "pending" ? "bg-amber-500/10 text-amber-400 border-amber-500/20" : "bg-rose-500/10 text-rose-400 border-rose-500/20"}`}>
              {pay.status === "approved" ? "Aprobado" : pay.status === "pending" ? "Pendiente" : pay.status === "rejected" ? "Rechazado" : pay.status}
            </Badge>
            <span className="text-zinc-300 flex-1 truncate">{pay.plan_name}{pay.reason ? ` - ${pay.reason}` : ""}</span>
            <span className={`font-mono text-sm font-bold shrink-0 ${pay.plan_balance >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
              {pay.plan_balance >= 0 ? "+" : ""}${Math.abs(pay.plan_balance || 0).toLocaleString("es-AR")}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
